import React, { useState, useEffect } from 'react';
import { 
  DocumentTextIcon,
  DevicePhoneMobileIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  DocumentArrowDownIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

import KPICard from '../components/KPICard';
import ChartContainer from '../components/ChartContainer';
import PieChart from '../components/charts/PieChart';
import LineChart from '../components/charts/LineChart';
import GenericDataTable from '../components/GenericDataTable';
import companyDataService from '../services/companyDataService';
import exportService from '../services/exportService';
import pdfExportService from '../services/pdfExportService';

const CompanyReport = () => {
  const [stats, setStats] = useState({
    totalProdutos: 0,
    totalConsultas: 0,
    totalDispositivos: 0,
    totalFiliais: 0,
    mediaConsultasPorProduto: 0
  });
  
  const [consultasPorFilial, setConsultasPorFilial] = useState([]);
  const [produtosPorCategoria, setProdutosPorCategoria] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [produtosMaisConsultados, setProdutosMaisConsultados] = useState([]);
  const [estatisticasFiliais, setEstatisticasFiliais] = useState([]);
  const [estatisticasDispositivos, setEstatisticasDispositivos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados para filtros
  const [filters, setFilters] = useState({
    filial: '',
    dispositivo: '',
    minConsultas: '',
    busca: ''
  });

  const [availableFiliais, setAvailableFiliais] = useState([]);
  const [availableDispositivos, setAvailableDispositivos] = useState([]);

  useEffect(() => {
    loadCompanyData();
  }, []);

  const loadCompanyData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await companyDataService.loadData();
      updateDashboardData();
      
      // Carregar opções para filtros
      setAvailableFiliais(companyDataService.getUniqueFiliais());
      setAvailableDispositivos(companyDataService.getUniqueDispositivos());
      
    } catch (error) {
      console.error('Erro ao carregar dados da empresa:', error);
      setError('Erro ao carregar dados da empresa. Verifique se o arquivo JSON está disponível.');
    } finally {
      setIsLoading(false);
    }
  };

  const updateDashboardData = () => {
    const stats = companyDataService.getStats();
    const consultasFilial = companyDataService.getConsultasPorFilial();
    const produtosCategoria = companyDataService.getProdutosPorCategoria();
    const timeline = companyDataService.getTimelineConsultas();
    const topProdutos = companyDataService.getProdutosMaisConsultados(15);
    const statsFiliais = companyDataService.getEstatisticasPorFilial();
    const statsDispositivos = companyDataService.getEstatisticasPorDispositivo();

    setStats(stats);
    setConsultasPorFilial(consultasFilial);
    setProdutosPorCategoria(produtosCategoria);
    setTimelineData(timeline);
    setProdutosMaisConsultados(topProdutos);
    setEstatisticasFiliais(statsFiliais);
    setEstatisticasDispositivos(statsDispositivos);
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    
    // Aplicar filtros
    companyDataService.filterData(newFilters);
    updateDashboardData();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      filial: '',
      dispositivo: '',
      minConsultas: '',
      busca: ''
    };
    setFilters(resetFilters);
    companyDataService.resetFilters();
    updateDashboardData();
  };

  const handleExportReport = async () => {
    try {
      const data = companyDataService.getFilteredData();
      const reportData = {
        stats,
        produtosMaisConsultados,
        estatisticasFiliais,
        estatisticasDispositivos,
        data
      };
      
      await exportService.exportToExcel(reportData, `relatorio_empresa_${Date.now()}.xlsx`);
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
    }
  };

  const handleExportPDF = async () => {
    try {
      const data = companyDataService.getFilteredData();
      const reportData = {
        stats,
        produtosMaisConsultados,
        estatisticasFiliais,
        estatisticasDispositivos,
        data
      };
      
      await pdfExportService.exportCompanyReport(reportData);
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
    }
  };

  const handleUpdateData = async () => {
    try {
      await companyDataService.updateFromApi();
      updateDashboardData();
      return { success: true, message: 'Dados atualizados com sucesso!' };
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
      throw error;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-dark-text mb-2">Carregando Relatório</h3>
          <p className="text-dark-text-secondary">
            Processando dados da empresa...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="card-dark p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-danger rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-dark-text mb-2">Erro ao Carregar</h3>
          <p className="text-dark-text-secondary mb-4">{error}</p>
          <button
            onClick={loadCompanyData}
            className="btn-primary"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  // Colunas para tabela de produtos mais consultados
  const produtosColumns = [
    { key: 'codbar', label: 'Código de Barras', sortable: true },
    { key: 'description', label: 'Descrição', sortable: true },
    { key: 'apelido_dispositivo', label: 'Dispositivo', sortable: true },
    { key: 'num_consultas', label: 'Consultas', sortable: true, type: 'number' },
    { key: 'filial', label: 'Filial', sortable: true }
  ];

  // Colunas para tabela de filiais
  const filiaisColumns = [
    { key: 'filial', label: 'Filial', sortable: true },
    { key: 'produtos', label: 'Produtos', sortable: true, type: 'number' },
    { key: 'consultas', label: 'Total Consultas', sortable: true, type: 'number' },
    { key: 'dispositivos', label: 'Dispositivos', sortable: true, type: 'number' },
    { key: 'mediaConsultas', label: 'Média Consultas', sortable: true, type: 'number' }
  ];

  // Colunas para tabela de dispositivos
  const dispositivosColumns = [
    { key: 'dispositivo', label: 'Dispositivo', sortable: true },
    { key: 'produtos', label: 'Produtos', sortable: true, type: 'number' },
    { key: 'consultas', label: 'Total Consultas', sortable: true, type: 'number' },
    { key: 'filiais', label: 'Filiais', sortable: true, type: 'number' },
    { key: 'mediaConsultas', label: 'Média Consultas', sortable: true, type: 'number' }
  ];

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-dark-text mb-2">
            Relatório da Empresa
          </h1>
          <p className="text-dark-text-secondary">
            ID: {companyDataService.companyId}
          </p>
        </div>
                 <div className="flex items-center gap-3">
           <button
             onClick={handleExportPDF}
             className="btn-primary flex items-center gap-2"
           >
             <DocumentArrowDownIcon className="w-5 h-5" />
             Exportar PDF
           </button>
           <button
             onClick={handleExportReport}
             className="btn-secondary flex items-center gap-2"
           >
             <DocumentArrowDownIcon className="w-5 h-5" />
             Exportar Excel
           </button>
         </div>
      </div>

      {/* Filtros */}
      <div className="card-dark p-6">
        <h3 className="text-lg font-semibold text-dark-text mb-4">Filtros</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-dark-text-secondary mb-2">
              Filial
            </label>
            <select
              value={filters.filial}
              onChange={(e) => handleFilterChange('filial', e.target.value)}
              className="input-dark w-full"
            >
              <option value="">Todas as filiais</option>
              {availableFiliais.map(filial => (
                <option key={filial} value={filial}>
                  Filial {filial}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-text-secondary mb-2">
              Dispositivo
            </label>
            <select
              value={filters.dispositivo}
              onChange={(e) => handleFilterChange('dispositivo', e.target.value)}
              className="input-dark w-full"
            >
              <option value="">Todos os dispositivos</option>
              {availableDispositivos.slice(0, 20).map(dispositivo => (
                <option key={dispositivo} value={dispositivo}>
                  {dispositivo}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-text-secondary mb-2">
              Mín. Consultas
            </label>
            <input
              type="number"
              value={filters.minConsultas}
              onChange={(e) => handleFilterChange('minConsultas', parseInt(e.target.value) || '')}
              placeholder="Ex: 10"
              className="input-dark w-full"
              min="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-text-secondary mb-2">
              Buscar Produto
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-text-secondary" />
              <input
                type="text"
                value={filters.busca}
                onChange={(e) => handleFilterChange('busca', e.target.value)}
                placeholder="Código ou descrição..."
                className="input-dark w-full pl-10"
              />
            </div>
          </div>
        </div>
        
        <div className="mt-4">
          <button
            onClick={handleResetFilters}
            className="btn-secondary text-sm"
          >
            Limpar Filtros
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <KPICard
          title="Total de Produtos"
          value={companyDataService.formatNumber(stats.totalProdutos)}
          icon={DocumentTextIcon}
          color="primary"
        />
        <KPICard
          title="Total de Consultas"
          value={companyDataService.formatNumber(stats.totalConsultas)}
          icon={ChartBarIcon}
          color="success"
        />
        <KPICard
          title="Dispositivos Ativos"
          value={companyDataService.formatNumber(stats.totalDispositivos)}
          icon={DevicePhoneMobileIcon}
          color="warning"
        />
        <KPICard
          title="Filiais"
          value={companyDataService.formatNumber(stats.totalFiliais)}
          icon={BuildingStorefrontIcon}
          color="info"
        />
        <KPICard
          title="Média Consultas/Produto"
          value={stats.mediaConsultasPorProduto.toFixed(1)}
          icon={ChartBarIcon}
          color="secondary"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Consultas por Filial */}
        <ChartContainer title="Consultas por Filial">
          {consultasPorFilial.length > 0 && (
            <PieChart data={consultasPorFilial.slice(0, 10)} />
          )}
        </ChartContainer>

        {/* Produtos por Categoria */}
        <ChartContainer title="Produtos por Categoria">
          {produtosPorCategoria.length > 0 && (
            <PieChart data={produtosPorCategoria} />
          )}
        </ChartContainer>
      </div>

      {/* Timeline */}
      <div className="grid grid-cols-1 gap-6">
        <ChartContainer title="Timeline de Consultas (Simulado)">
          {timelineData.length > 0 && (
            <LineChart data={timelineData} />
          )}
        </ChartContainer>
      </div>

      {/* Tabelas */}
      <div className="space-y-6">
        {/* Produtos Mais Consultados */}
        <div className="card-dark">
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-dark-text">
              Produtos Mais Consultados
            </h3>
          </div>
          <GenericDataTable
            data={produtosMaisConsultados}
            columns={produtosColumns}
            pageSize={10}
          />
        </div>

        {/* Estatísticas por Filial */}
        <div className="card-dark">
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-dark-text">
              Estatísticas por Filial
            </h3>
          </div>
          <GenericDataTable
            data={estatisticasFiliais}
            columns={filiaisColumns}
            pageSize={10}
          />
        </div>

        {/* Estatísticas por Dispositivo */}
        <div className="card-dark">
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-dark-text">
              Estatísticas por Dispositivo
            </h3>
          </div>
          <GenericDataTable
            data={estatisticasDispositivos.slice(0, 20)}
            columns={dispositivosColumns}
            pageSize={10}
          />
        </div>
      </div>
    </div>
  );
};

export default CompanyReport;
